import type { InputEvent } from '@br/protocol';
import { modifiersOf, type ModifierKeys } from './mouse';

export type InputKey = Extract<InputEvent, { t: 'input.key' }>;
export type InputIme = Extract<InputEvent, { t: 'input.ime' }>;
export type InputText = Extract<InputEvent, { t: 'input.text' }>;

/** The subset of KeyboardEvent the mapper needs (so tests can pass plain objects). */
export interface KeyLike extends ModifierKeys {
  key: string;
  code: string;
  keyCode: number;
  repeat?: boolean;
  isComposing?: boolean;
}

/** A single grapheme such as "a", "가", "€" or an emoji. */
export function isPrintableKey(e: Pick<KeyLike, 'key'>): boolean {
  return [...e.key].length === 1;
}

/**
 * Text a keydown should carry so the remote page also gets a `keypress`/`char`. Follows the
 * Puppeteer/Playwright rule: any modifier other than Shift means "shortcut", not text.
 */
export function textForKey(e: KeyLike): string | undefined {
  if (e.ctrlKey || e.altKey || e.metaKey) return undefined;
  if (isPrintableKey(e)) return e.key;
  if (e.key === 'Enter') return '\r';
  return undefined;
}

/**
 * Keydowns that must stay with the local browser. We neither cancel them nor forward them:
 * F5/F11/F12 and the DevTools chords, plus paste (handled by the textarea's `beforeinput`, so the
 * *local* clipboard ends up in the remote page).
 */
export function isPassThrough(e: KeyLike): boolean {
  if (e.key === 'F5' || e.key === 'F11' || e.key === 'F12') return true;
  const k = e.key.toLowerCase();
  const primary = e.ctrlKey || e.metaKey;
  if (primary && e.shiftKey && (k === 'i' || k === 'j' || k === 'c')) return true;
  if (e.metaKey && e.altKey && (k === 'i' || k === 'j' || k === 'c')) return true;
  if (isLocalPaste(e)) return true;
  return false;
}

/** Ctrl+V / Cmd+V: let the local paste happen so `beforeinput` can forward the clipboard text. */
export function isLocalPaste(e: KeyLike): boolean {
  return (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'v';
}

function vk(e: KeyLike): number {
  const n = Number.isFinite(e.keyCode) ? e.keyCode : 0;
  return Math.max(0, Math.min(255, n));
}

/**
 * keydown -> `input.key`, or null when the event belongs to an IME composition
 * (`isComposing` or the synthetic 229 keyCode) and must be ignored.
 */
export function mapKeyDown(tabId: string, e: KeyLike): InputKey | null {
  if (e.isComposing || e.keyCode === 229) return null;
  const text = textForKey(e);
  return {
    t: 'input.key',
    tabId,
    type: text === undefined ? 'rawKeyDown' : 'keyDown',
    key: e.key.slice(0, 32),
    code: e.code.slice(0, 32),
    windowsVirtualKeyCode: vk(e),
    text,
    modifiers: modifiersOf(e),
    autoRepeat: Boolean(e.repeat),
  };
}

export function mapKeyUp(tabId: string, e: KeyLike): InputKey | null {
  if (e.isComposing || e.keyCode === 229) return null;
  return {
    t: 'input.key',
    tabId,
    type: 'keyUp',
    key: e.key.slice(0, 32),
    code: e.code.slice(0, 32),
    windowsVirtualKeyCode: vk(e),
    modifiers: modifiersOf(e),
    autoRepeat: false,
  };
}

/** compositionupdate -> `input.ime` (caret at the end of the composed text) */
export function mapComposition(tabId: string, text: string): InputIme {
  const t = text.slice(0, 256);
  return { t: 'input.ime', tabId, text: t, selectionStart: t.length, selectionEnd: t.length };
}

/** compositionend / paste / mobile insertText -> `input.text` (empty string cancels a composition) */
export function mapText(tabId: string, text: string): InputText {
  return { t: 'input.text', tabId, text: text.slice(0, 65536) };
}
